/* Calista — premium layer behaviours
   1. Sticky header that hides on scroll down, reveals on scroll up
   2. Sticky add-to-bag on product pages (mobile + desktop); its Buy now presses
      Fastrr's own button — 2b. gallery progress line on phones
   3. USP marquee duplication (seamless loop)
   4. Copy a discount code (product page offer block, sticky bar) — display only, applies nothing
   5. Floating product video on product pages
   6. Recently viewed products on product pages
   7. "Similar styles" rail: "View all" → the product's category; "View similar" pill hides if the rail is empty
   8. Video reels (sections/cal-reels.liquid): play muted while on screen, tap for sound
   9. Footer columns: accordions on phones, always open on wider screens
   10. Carousel auto-rotate (featured collection, "Auto-rotate" setting)
*/

(function () {
  'use strict';

  var root = document.documentElement;

  /* 2 ── Sticky add-to-bag ------------------------------------------------ */
  function initStickyAtc() {
    var bar = document.querySelector('.cal-sticky-atc');
    if (!bar || bar.dataset.calReady === 'true') return;

    var anchor = document.querySelector('.product-form__buttons');
    if (!anchor || !('IntersectionObserver' in window)) return;
    bar.dataset.calReady = 'true';

    // --cal-sticky-h reserves room under the footer; --cal-sticky-offset lifts
    // the floating video only while the bar is actually showing.
    function measure() {
      root.style.setProperty('--cal-sticky-h', bar.offsetHeight + 'px');
      root.style.setProperty(
        '--cal-sticky-offset',
        bar.classList.contains('is-visible') ? bar.offsetHeight + 'px' : '0px'
      );
    }

    function setVisible(visible) {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      bar.inert = !visible;
      measure();
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Show the bar whenever the real buy buttons are off-screen — above
          // (scrolled past) or below (not reached yet, e.g. under the gallery
          // on a phone) — so a buy button is always in reach.
          setVisible(!entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
    measure();
    window.addEventListener('resize', measure, { passive: true });

    // Delegates to the theme's own button, which Fastrr hooks. Never adds to
    // the cart or opens checkout by itself.
    var button = bar.querySelector('.cal-sticky-atc__button');
    if (button) {
      button.addEventListener('click', function (event) {
        var submit = document.querySelector('.product-form__buttons .product-form__submit');
        if (!submit || submit.disabled) return;
        event.preventDefault();
        submit.click();
      });
    }

    // Buy now = the brand's own payment only: press Fastrr's Buy now, which
    // Shiprocket renders inside the product form (snippets/buy-buttons.liquid,
    // .shiprocket-headless[data-type="product"]). Fastrr reads the variant and
    // quantity from that form. Nothing is added to the cart and no other
    // checkout (/checkout, Shopify's Buy it now) is ever opened. If Fastrr's
    // button hasn't rendered yet, bring the real buy box into view instead.
    var buyNow = bar.querySelector('[data-cal-sticky-buy]');
    if (buyNow) {
      buyNow.addEventListener('click', function (event) {
        event.preventDefault();
        var fastrr = document.querySelector(
          '.product-form__buttons .shiprocket-headless[data-type="product"] .sr-headless-checkout'
        );
        if (fastrr && !fastrr.disabled) {
          fastrr.click();
          return;
        }
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        anchor.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      });
    }
  }

  /* 2b ── Gallery progress line (phones) ------------------------------------ */
  // Each main-gallery slide carries its own position (1/n … n/n) so the thin
  // line along the bottom of the image shows where the shopper is.
  function initGalleryProgress() {
    document.querySelectorAll('.product__media-wrapper [id^="Slider-Gallery-"]').forEach(function (list) {
      var items = list.querySelectorAll('.product__media-item');
      if (items.length < 2) return;
      items.forEach(function (item, index) {
        item.style.setProperty('--cal-slide-progress', ((index + 1) / items.length).toFixed(4));
      });
    });
  }

  /* 3 ── USP marquee ------------------------------------------------------ */
  function initMarquee() {
    document.querySelectorAll('.cal-usp--scroll').forEach(function (usp) {
      var viewport = usp.querySelector('.cal-usp__viewport');
      var track = usp.querySelector('.cal-usp__track');
      if (!viewport || !track || viewport.dataset.calCloned === 'true') return;

      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      viewport.appendChild(clone);
      viewport.dataset.calCloned = 'true';
    });
  }

  /* 4 ── Copy discount code ---------------------------------------------- */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      var ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (error) {
        ok = false;
      }
      document.body.removeChild(field);
      if (ok) {
        resolve();
      } else {
        reject();
      }
    });
  }

  function initCopyCode() {
    if (document.documentElement.dataset.calCopyBound === 'true') return;
    document.documentElement.dataset.calCopyBound = 'true';

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-cal-copy]');
      if (!button) return;

      var action = button.querySelector('.cal-pdp-offer__code-action, [data-cal-copy-action]');
      copyText(button.dataset.calCopy).then(function () {
        if (!action) return;
        if (!action.dataset.calLabel) action.dataset.calLabel = action.textContent;
        action.textContent = button.dataset.calCopied || action.dataset.calLabel;
        button.classList.add('is-copied');
        window.clearTimeout(button.calCopyTimer);
        button.calCopyTimer = window.setTimeout(function () {
          action.textContent = action.dataset.calLabel;
          button.classList.remove('is-copied');
        }, 2000);
      }, function () {});
    });
  }

  /* 5 ── Floating product video ------------------------------------------ */
  // Muted autoplay is the only autoplay browsers allow without a gesture, so
  // the corner copy is always muted; sound comes only in the expanded player.
  function initFloatVideo(widget) {
    if (widget.dataset.calReady === 'true') return;
    widget.dataset.calReady = 'true';

    var video = widget.querySelector('[data-cal-fv-video]');
    var card = widget.querySelector('[data-cal-fv-card]');
    var expandBtn = widget.querySelector('[data-cal-fv-expand]');
    var closeBtn = widget.querySelector('[data-cal-fv-dismiss]');
    var backdrop = document.querySelector('[data-cal-fv-backdrop]');
    if (!video || !card) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var storeKey = 'cal-fv-dismissed-' + (widget.dataset.productId || 'x');
    var showAfter = parseInt(widget.dataset.showAfter, 10);
    if (isNaN(showAfter)) showAfter = 400;

    try {
      if (sessionStorage.getItem(storeKey) === '1') {
        widget.remove();
        return;
      }
    } catch (error) {
      // Storage blocked: treat as not dismissed.
    }

    var expanded = false;
    var revealed = false;
    var hiddenByReels = false;
    var dismissed = false;

    function play() {
      if (dismissed) return;
      if (reduceMotion && !expanded) {
        // Reduced motion: the corner copy never moves on its own.
        video.pause();
        widget.classList.add('is-paused');
        return;
      }
      var attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch(function () {
          // Autoplay refused (data saver, low power): show the play control.
          widget.classList.add('is-paused');
        });
      }
    }

    function reveal() {
      if (revealed) return;
      revealed = true;
      widget.classList.add('is-visible');
      play();
      window.removeEventListener('scroll', onScroll);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        if (window.pageYOffset >= showAfter) reveal();
      });
    }

    function onKey(event) {
      if (event.key === 'Escape') collapse();
    }

    function expand() {
      if (expanded) return;
      expanded = true;
      reveal();
      widget.classList.add('is-expanded');
      widget.classList.remove('is-paused');
      video.muted = false;
      video.controls = true;
      video.currentTime = 0;
      if (backdrop) {
        backdrop.hidden = false;
        window.requestAnimationFrame(function () {
          backdrop.classList.add('is-open');
        });
      }
      document.addEventListener('keydown', onKey);
      play();
      if (closeBtn) closeBtn.focus();
    }

    function collapse() {
      if (!expanded) return;
      expanded = false;
      widget.classList.remove('is-expanded');
      video.muted = true;
      video.controls = false;
      if (backdrop) {
        backdrop.classList.remove('is-open');
        window.setTimeout(function () {
          backdrop.hidden = true;
        }, 260);
      }
      document.removeEventListener('keydown', onKey);
      play();
      if (expandBtn) expandBtn.focus();
    }

    function dismiss(event) {
      event.stopPropagation();
      if (expanded) {
        collapse();
        return;
      }
      dismissed = true;
      video.pause();
      widget.classList.remove('is-visible');
      try {
        sessionStorage.setItem(storeKey, '1');
      } catch (error) {
        // Still hidden for this page view.
      }
      window.setTimeout(function () {
        widget.remove();
      }, 340);
    }

    card.addEventListener('click', function (event) {
      if (event.target.closest('[data-cal-fv-dismiss]') || expanded) return;
      expand();
    });
    if (closeBtn) closeBtn.addEventListener('click', dismiss);
    if (backdrop) backdrop.addEventListener('click', collapse);

    video.addEventListener('playing', function () {
      if (!expanded) widget.classList.remove('is-paused');
    });

    // Don't keep a video running on a tab nobody is looking at.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        video.pause();
      } else if (revealed && !hiddenByReels) {
        play();
      }
    });

    // ReelUp plays its own videos lower down the page: step aside while it
    // is on screen so two videos never compete.
    var reels = document.querySelector('[id*="reelup"]');
    if (reels && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (expanded || dismissed) return;
            hiddenByReels = entry.isIntersecting;
            if (hiddenByReels) {
              video.pause();
              widget.classList.remove('is-visible');
            } else if (revealed) {
              widget.classList.add('is-visible');
              play();
            }
          });
        },
        { threshold: 0.25 }
      ).observe(reels);
    }

    if (showAfter === 0 || window.pageYOffset >= showAfter) {
      reveal();
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* 6 ── Recently viewed products ---------------------------------------- */
  // Only handles are stored. Every card is rendered fresh by the theme
  // (sections/cal-rv-card.liquid), so prices and stock are never stale.
  var RV_KEY = 'cal-recently-viewed';
  var RV_MAX = 12;

  function rvRead() {
    try {
      var list = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function rvWrite(list) {
    try {
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
    } catch (error) {
      // Storage blocked: the section simply stays hidden.
    }
  }

  function initRecentlyViewed(section) {
    if (section.dataset.calReady === 'true') return;
    section.dataset.calReady = 'true';

    var list = section.querySelector('[data-cal-rv-list]');
    var current = section.dataset.handle || '';
    var limit = parseInt(section.dataset.limit, 10) || 4;
    var stored = rvRead();

    // Record this product first, so it counts even if nothing else renders.
    if (current) {
      rvWrite([current].concat(stored.filter(function (handle) { return handle !== current; })));
    }

    var wanted = stored.filter(function (handle) { return handle && handle !== current; }).slice(0, limit);
    if (!list || !wanted.length || !window.fetch || !window.DOMParser) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var missing = [];

    Promise.all(
      wanted.map(function (handle) {
        return fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=cal-rv-card')
          .then(function (response) {
            if (!response.ok) {
              missing.push(handle);
              return '';
            }
            return response.text();
          })
          .catch(function () {
            return '';
          });
      })
    ).then(function (responses) {
      var parser = new DOMParser();
      var cards = [];
      responses.forEach(function (html) {
        if (!html) return;
        var card = parser.parseFromString(html, 'text/html').querySelector('.grid__item');
        if (card) cards.push(card);
      });

      // Drop products that no longer exist (deleted or unpublished).
      if (missing.length) {
        rvWrite(rvRead().filter(function (handle) { return missing.indexOf(handle) === -1; }));
      }

      if (!cards.length) return;
      cards.forEach(function (card) {
        list.appendChild(card);
      });
      section.hidden = false;
    });
  }

  /* 7 ── Similar styles rail ---------------------------------------------- */
  // The rail (sections/related-products.liquid, #cal-similar) is filled by the
  // theme's ProductRecommendations when it scrolls into view. Once it has
  // cards, "View all" takes the category link from the buy box eyebrow. If it
  // still has none a few seconds after coming into view (the theme leaves an
  // empty rail untouched), the gallery's "View similar" pill is removed.
  function initSimilar() {
    var section = document.getElementById('cal-similar');
    if (!section || section.dataset.calReady === 'true') return;
    var rec = section.querySelector('product-recommendations');
    if (!rec) return;
    section.dataset.calReady = 'true';

    function hasCards() {
      return !!rec.querySelector('.grid__item');
    }

    function pointViewAll() {
      var category = document.querySelector('[data-cal-category-link]');
      if (!category) return;
      rec.querySelectorAll('[data-cal-view-all]').forEach(function (link) {
        link.setAttribute('href', category.getAttribute('href'));
      });
    }

    if (hasCards()) {
      pointViewAll();
      return;
    }

    if ('MutationObserver' in window) {
      var mutations = new MutationObserver(function () {
        if (!hasCards()) return;
        mutations.disconnect();
        pointViewAll();
      });
      mutations.observe(rec, { childList: true, subtree: true });
    }

    if ('IntersectionObserver' in window) {
      var seen = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        seen.disconnect();
        window.setTimeout(function () {
          if (hasCards()) return;
          document.querySelectorAll('[data-cal-view-similar]').forEach(function (pill) {
            pill.hidden = true;
          });
        }, 5000);
      });
      seen.observe(rec);
    }
  }

  /* 8 ── Video reels ------------------------------------------------------ */
  // Cards play muted (the only autoplay browsers allow) while at least a third
  // of the card is on screen, and pause off screen. A tap is a user gesture, so
  // it may unmute: that card switches to its full clip (if it was showing the
  // lighter preview) and plays with sound; every other card is muted. With
  // reduced motion nothing autoplays — a tap plays the clip with sound.
  function initReels(root) {
    if (root.dataset.calReady === 'true') return;
    root.dataset.calReady = 'true';

    var reels = Array.prototype.slice.call(root.querySelectorAll('[data-cal-reel]'));
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function play(video) {
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    }

    function setSound(reel, on) {
      var video = reel.querySelector('video');
      var toggle = reel.querySelector('[data-cal-reel-toggle]');
      var text = reel.querySelector('[data-cal-reel-sound-text]');
      if (!video) return;
      if (on) {
        var full = video.dataset.full;
        if (full && video.getAttribute('src') !== full) {
          video.setAttribute('src', full);
          video.load();
        }
        video.muted = false;
        play(video);
      } else {
        video.muted = true;
        if (reduce) video.pause();
      }
      reel.classList.toggle('is-sound', on);
      if (toggle) toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (text) text.textContent = on ? text.dataset.on : text.dataset.off;
    }

    reels.forEach(function (reel) {
      var toggle = reel.querySelector('[data-cal-reel-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        var on = !reel.classList.contains('is-sound');
        reels.forEach(function (other) {
          if (other !== reel && other.classList.contains('is-sound')) setSound(other, false);
        });
        setSound(reel, on);
      });
    });

    if (reduce || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) {
            play(video);
          } else {
            if (entry.target.classList.contains('is-sound')) setSound(entry.target, false);
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    reels.forEach(function (reel) {
      observer.observe(reel);
    });
  }

  /* 9 ── Footer accordions ------------------------------------------------ */
  // Phones: each Calista footer column opens from its heading button. From
  // 750px every column is shown (CSS), so the button is taken out of the tab
  // order and reports itself as expanded.
  var footerWide = window.matchMedia('(min-width: 750px)');

  function syncFooterColumns() {
    document.querySelectorAll('[data-cal-fcol-toggle]').forEach(function (toggle) {
      var column = toggle.closest('.cal-fcol');
      if (footerWide.matches) {
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('tabindex', '-1');
      } else {
        toggle.setAttribute('aria-expanded', column && column.classList.contains('is-open') ? 'true' : 'false');
        toggle.removeAttribute('tabindex');
      }
    });
  }

  function initFooterColumns() {
    document.querySelectorAll('[data-cal-fcol-toggle]').forEach(function (toggle) {
      if (toggle.dataset.calReady === 'true') return;
      toggle.dataset.calReady = 'true';
      toggle.addEventListener('click', function () {
        if (footerWide.matches) return;
        var column = toggle.closest('.cal-fcol');
        if (!column) return;
        var open = !column.classList.contains('is-open');
        column.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    if (root.dataset.calFcolBound !== 'true') {
      root.dataset.calFcolBound = 'true';
      if (footerWide.addEventListener) {
        footerWide.addEventListener('change', syncFooterColumns);
      } else if (footerWide.addListener) {
        footerWide.addListener(syncFooterColumns);
      }
    }
    syncFooterColumns();
  }

  /* 10 ── Carousel auto-rotate --------------------------------------------- */
  // Moves a theme slider (slider-component with data-cal-autoplay="<seconds>")
  // one card at a time and wraps to the start at the end. It waits while the
  // carousel is off screen, hovered or focused, for 6 s after a touch, swipe or
  // arrow click, and while the tab is hidden. Off under reduced motion.
  function initAutoplay(slider) {
    if (slider.dataset.calReady === 'true') return;
    var seconds = parseInt(slider.dataset.calAutoplay, 10);
    if (!seconds) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var list = slider.querySelector('.slider');
    if (!list) return;
    slider.dataset.calReady = 'true';

    var visible = !('IntersectionObserver' in window);
    var hovering = false;
    var holdUntil = 0;

    function hold() {
      holdUntil = Date.now() + 6000;
    }

    function step() {
      if (!visible || hovering || document.hidden || Date.now() < holdUntil) return;
      if (list.scrollWidth - list.clientWidth < 4) return;
      var item = list.querySelector('.slider__slide');
      if (!item) return;
      var gap = parseFloat(window.getComputedStyle(list).columnGap) || 0;
      var atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
      list.scrollTo({ left: atEnd ? 0 : list.scrollLeft + item.getBoundingClientRect().width + gap, behavior: 'smooth' });
    }

    slider.addEventListener('mouseenter', function () { hovering = true; });
    slider.addEventListener('mouseleave', function () { hovering = false; });
    slider.addEventListener('focusin', function () { hovering = true; });
    slider.addEventListener('focusout', function () { hovering = false; });
    ['pointerdown', 'touchstart', 'wheel'].forEach(function (type) {
      list.addEventListener(type, hold, { passive: true });
    });
    slider.querySelectorAll('.slider-button').forEach(function (button) {
      button.addEventListener('click', hold);
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
        },
        { threshold: 0.3 }
      ).observe(slider);
    }

    window.setInterval(step, seconds * 1000);
  }

  function init() {
    initStickyAtc();
    initGalleryProgress();
    initMarquee();
    initCopyCode();
    initSimilar();
    initFooterColumns();
    document.querySelectorAll('[data-cal-autoplay]').forEach(initAutoplay);
    document.querySelectorAll('[data-cal-reels]').forEach(initReels);
    document.querySelectorAll('[data-cal-float-video]').forEach(initFloatVideo);
    document.querySelectorAll('[data-cal-rv]').forEach(initRecentlyViewed);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run inside the theme editor when sections are re-rendered
  document.addEventListener('shopify:section:load', init);
})();
